import React, {useMemo} from 'react'
import styles from './UIGameBankPortrait.module.scss'
import BankIcon from '@static/images/bank.svg'
import {UIGameCardInventory} from '../../common/UIGameCardInventory/UIGameCardInventory'
import {CardEnum, CardStateOrdered, CardStateUnordered} from '@colonist/game-states'
import {ModeSettingEnum} from '@colonist/types'
import {getBankCardTooltip, BankCardTooltipResult} from '../UIGameBank/getBankCardTooltip'
import {useUIGameTranslationContext} from '../../contexts/UIGameTranslationContext'

export interface UIGameBankPortraitProps {
    cardState: CardStateUnordered
    cardStackSizes: CardStateUnordered
    hideBankCards: boolean
    modeSetting: ModeSettingEnum
    progressCards: CardStateOrdered | undefined
}

export const UIGameBankPortrait = ({
    cardState,
    cardStackSizes,
    hideBankCards,
    modeSetting,
    progressCards,
}: UIGameBankPortraitProps) => {
    const {translationKeyContext} = useUIGameTranslationContext()

    const cardTooltipOverrides = useMemo(() => {
        const overrides: {[key in CardEnum]?: BankCardTooltipResult} = {}
        for(const cardEnumStr of Object.keys(cardState)) {
            const cardEnum = Number(cardEnumStr) as CardEnum
            const count = cardState[cardEnum] ?? 0
            overrides[cardEnum] = getBankCardTooltip(cardEnum, count, hideBankCards, modeSetting, progressCards, translationKeyContext)
        }
        return overrides
    }, [cardState, hideBankCards, modeSetting, progressCards, translationKeyContext])

    // Cards with count 0 are excluded by CardValidatorUnordered, so we transform
    // the data to keep them visible as greyed-out single cards with no badge.
    const {displayCardState, displayCardStackSizes, emptyCardEnums, cardsToDisable} = useMemo(() => {
        const displayState: CardStateUnordered = {}
        const displayStackSizes: CardStateUnordered = {}
        const emptyEnums: CardEnum[] = []
        const disable: CardStateUnordered = {}

        for(const cardEnumStr of Object.keys(cardState)) {
            const cardEnum = Number(cardEnumStr) as CardEnum
            const count = cardState[cardEnum] ?? 0

            if(count === 0) {
                displayState[cardEnum] = 1
                displayStackSizes[cardEnum] = 1
                emptyEnums.push(cardEnum)
                disable[cardEnum] = 1
            } else {
                displayState[cardEnum] = count
                displayStackSizes[cardEnum] = cardStackSizes[cardEnum] ?? count
            }
        }

        return {displayCardState: displayState, displayCardStackSizes: displayStackSizes, emptyCardEnums: emptyEnums, cardsToDisable: disable}
    }, [cardState, cardStackSizes])

    return (
        <div id="bank-container" className={styles.container}>
            <img src={BankIcon} className={styles.bankIcon} data-bank-icon />
            <UIGameCardInventory
                cardSize="xxs"
                cardStackSizes={displayCardStackSizes}
                cardStackDirection='vertical'
                enablePartition={false}
                cardState={displayCardState}
                cardsToDisable={cardsToDisable}
                cardsToHideBadge={emptyCardEnums}
                forceShowCardBadges={!hideBankCards}
                cardTooltipOverrides={cardTooltipOverrides}
            />
        </div>
    )
}
